(ns frontend.view.task.webentry-resolution.ai-components
  "AI suggestion UI components for task resolution.

   These components display AI-generated suggestions with confidence
   indicators and allow users to accept, modify, or dismiss them."
  (:require
   [common.localization :refer [tr]]
   [reagent.core :as r]))

;; =============================================================================
;; Confidence Indicators
;; =============================================================================

(defn confidence-color
  "Get CSS color class based on confidence level."
  [confidence]
  (cond
    (>= confidence 0.9) "text-success-regular"
    (>= confidence 0.7) "text-warning-regular"
    :else "text-danger-regular"))

(defn confidence-bg-color
  "Get CSS background color class based on confidence level."
  [confidence]
  (cond
    (>= confidence 0.9) "bg-success-lighter"
    (>= confidence 0.7) "bg-warning-lighter"
    :else "bg-danger-lighter"))

(defn confidence-indicator
  "Visual indicator for AI confidence level.
   Shows percentage with appropriate color coding."
  [{:keys [confidence]}]
  (let [pct (int (* (or confidence 0) 100))
        color (confidence-color confidence)]
    [:span {:class (str "text-xs font-medium " color)}
     (str pct "%")]))

;; =============================================================================
;; AI Badge Components
;; =============================================================================

(defn ai-badge
  "Small badge indicating a field was pre-filled by AI.
   Shows confidence score and reasoning on hover."
  [{:keys [confidence reasoning]}]
  (let [tooltip-text (str (tr :ai/confidence (int (* (or confidence 0) 100)))
                          (when reasoning
                            (str "\n" reasoning)))]
    [:span.inline-flex.items-center.gap-1.px-1.5.py-0.5.rounded.text-xs.cursor-help
     {:class "bg-primary-lighter text-primary-regular"
      :title tooltip-text}
     [:i.fa.fa-robot]
     "AI"
     [confidence-indicator {:confidence confidence}]]))

(defn ai-field-indicator
  "Indicator shown next to a field that has an AI suggestion.
   Use this when you want to show AI suggested a value for a field."
  [{:keys [confidence reasoning on-click]}]
  [:span.inline-flex.items-center.gap-1.ml-2.cursor-pointer
   {:class "text-primary-regular hover:text-primary-dark"
    :title (or reasoning (tr :ai/prefilled))
    :on-click on-click}
   [:i.fa.fa-robot.text-sm]
   [confidence-indicator {:confidence confidence}]])

;; =============================================================================
;; Banners and Indicators
;; =============================================================================

(defn ai-processing-indicator
  "Shows when AI is still processing the task.
   Displayed at the top of the resolution view."
  []
  [:div.flex.items-center.gap-3.p-4.mb-4.rounded-lg.border
   {:class "bg-base-lighter border-base-light"}
   [:div.flex.items-center.justify-center.w-10.h-10.rounded-full
    {:class "bg-primary-lighter"}
    [:i.fa.fa-robot.fa-lg.text-primary-regular.animate-pulse]]
   [:div.flex-1
    [:div.font-medium (tr :ai/processing)]
    [:div.text-sm.text-base-light-text-paler
     (tr :ai/can-proceed-manually)]]])

(defn ai-unavailable-banner
  "Banner shown when AI processing could not produce suggestions. Renders nil
   unless the task's AI status is token-exhausted or failed, so call sites can
   include it unconditionally."
  [{:keys [status]}]
  (when (contains? #{:ai.status/token-exhausted :ai.status/failed} status)
    [:div.flex.items-center.gap-3.p-4.mb-4.rounded-lg.border
     {:class "bg-warning-lighter border-warning-light"}
     [:div.flex.items-center.justify-center.w-10.h-10.rounded-full
      {:class "bg-warning-regular"}
      [:i.fa.fa-robot.fa-lg.text-white]]
     [:div.flex-1
      [:div.font-medium
       (tr (if (= status :ai.status/token-exhausted)
             :ai/unavailable-token-exhausted
             :ai/unavailable-failed))]
      [:div.text-sm.text-base-light-text-paler
       (tr :ai/unavailable-contact-admin)]]]))

(defn ai-suggestions-banner
  "Banner shown when AI suggestions are available.
   Provides options to apply all or dismiss."
  [{:keys [on-apply-all on-dismiss company-count contact-count deal-count]}]
  (let [total (+ (or company-count 0) (or contact-count 0) (or deal-count 0))]
    [:div.flex.items-center.justify-between.p-4.mb-4.rounded-lg.border
     {:class "bg-primary-lighter border-primary-light"}
     [:div.flex.items-center.gap-3
      [:div.flex.items-center.justify-center.w-10.h-10.rounded-full
       {:class "bg-primary-regular"}
       [:i.fa.fa-robot.fa-lg.text-white]]
      [:div
       [:div.font-medium.text-primary-dark
        (tr :ai/suggestions-available)]
       [:div.text-sm.text-primary-regular
        (str total " " (tr :ai/fields-prefilled))]]]
     [:div.flex.gap-2
      (when on-apply-all
        [:button.btn.btn-sm.btn-primary
         {:on-click on-apply-all}
         (tr :ai/apply-all)])
      (when on-dismiss
        [:button.btn.btn-sm.btn-secondary
         {:on-click on-dismiss}
         (tr :ai/dismiss)])]]))

;; =============================================================================
;; Field Value Display
;; =============================================================================

(defn ai-suggested-value
  "Display an AI-suggested value with accept/reject actions.
   Used in comparison views."
  [{:keys [field-key value confidence reasoning on-accept on-reject]}]
  [:div.flex.items-center.gap-2.p-2.rounded.border
   {:class (str (confidence-bg-color confidence) " border-base-light")}
   [:div.flex-1
    [:div.flex.items-center.gap-2
     [:span.font-medium (tr field-key)]
     [ai-badge {:confidence confidence :reasoning reasoning}]]
    [:div.mt-1 (str value)]]
   [:div.flex.gap-1
    (when on-accept
      [:button.btn.btn-xs.btn-success
       {:on-click on-accept
        :title (tr :ai/accept)}
       [:i.fa.fa-check]])
    (when on-reject
      [:button.btn.btn-xs.btn-danger-hollow
       {:on-click on-reject
        :title (tr :ai/reject)}
       [:i.fa.fa-times]])]])

;; =============================================================================
;; Entity Suggestion Cards
;; =============================================================================

(defn entity-operation-label
  "Get human-readable label for AI operation type."
  [operation entity-name]
  (case operation
    :create (tr :ai/suggests-create entity-name)
    :link-existing (tr :ai/suggests-link entity-name)
    :update-existing (tr :ai/suggests-update entity-name)
    (tr :ai/suggests-create entity-name)))

(defn ai-entity-suggestion-card
  "Card displaying an AI-suggested entity (company/contact/deal).
   Shows operation type, fields, and action buttons."
  [{:keys [entity-type operation suggestion existing-entity
           on-accept on-modify on-reject]}]
  (let [{:keys [fields temp-id]} suggestion
        display-name (or (:display-name existing-entity)
                         (get-in fields [:asset/name :value])
                         (get-in fields [:person/first-name :value])
                         (get-in fields [:oppo/name :value])
                         temp-id)]
    [:div.border.rounded-lg.p-4.mb-3
     {:class "border-primary-light bg-white"}
     ;; Header
     [:div.flex.items-center.justify-between.mb-3
      [:div.flex.items-center.gap-2
       [:span.font-semibold
        (entity-operation-label operation display-name)]
       [:span.px-2.py-0.5.rounded.text-xs
        {:class "bg-primary-lighter text-primary-regular"}
        [:i.fa.fa-robot.mr-1]
        "AI"]]
      [:div.flex.gap-2
       (when on-accept
         [:button.btn.btn-sm.btn-success
          {:on-click on-accept}
          (tr :ai/accept)])
       (when on-modify
         [:button.btn.btn-sm.btn-secondary
          {:on-click on-modify}
          (tr :ai/modify)])
       (when on-reject
         [:button.btn.btn-sm.btn-danger-hollow
          {:on-click on-reject}
          (tr :ai/reject)])]]
     ;; Fields
     [:div.space-y-2
      (for [[field-key field-data] fields]
        ^{:key field-key}
        [:div.flex.items-center.gap-2.py-1.border-b.border-base-lighter
         [:span.w-32.text-sm.text-base-light-text-paler
          (tr field-key)]
         [:span.flex-1.font-medium
          (str (:value field-data))]
         [confidence-indicator {:confidence (:confidence field-data)}]])]]))
